import React, { useState } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, Area, AreaChart,
  ScatterChart, Scatter,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ComposedChart, ReferenceLine,
  FunnelChart, Funnel, LabelList
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './ChartVisualization.css';

interface ChartData {
  type?: string;
  chart_type?: string;
  title?: string;
  chart_title?: string;
  data?: any;
  table_data?: {
    headers?: string[];
    rows?: any[][];
  };
  labels?: string[];
  datasets?: any[];
  xAxis?: string;
  yAxis?: string;
  description?: string;
  sources?: string[];
  insight_details?: string;
  insightDetails?: string;
  strategic_implication?: string;
  strategicImplication?: string;
}

interface TableData {
  title?: string;
  headers?: string[];
  rows?: any[][];
  data?: any[];
}

interface ChartVisualizationProps {
  charts?: ChartData[];
  tables?: TableData[];
  onRefresh?: () => void;
  canExport?: boolean;
}

// Color palette for charts
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFC658', '#8DD1E1', '#A4DE6C', '#FFD93D'
];

const ChartVisualization: React.FC<ChartVisualizationProps> = ({
  charts = [],
  tables = [],
  onRefresh,
  canExport = false
}) => {
  const [exporting, setExporting] = useState(false);

  const handleExportPDF = async () => {
    if (!canExport) return;
    
    setExporting(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 20;
      
      // Add title
      pdf.setFontSize(16);
      pdf.text('Report Export', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
      
      // Add generation date
      pdf.setFontSize(10);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, yPosition);
      yPosition += 15;
      
      // Export each chart
      const chartElements = document.querySelectorAll('.chart-container');
      for (let i = 0; i < chartElements.length; i++) {
        if (yPosition > pageHeight - 100) {
          pdf.addPage();
          yPosition = 20;
        }
        
        const canvas = await html2canvas(chartElements[i] as HTMLElement);
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = pageWidth - 40;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight + 10;
      }
      
      // Export tables
      if (tables.length > 0) {
        pdf.addPage();
        yPosition = 20;
        pdf.setFontSize(14);
        pdf.text('Tables', 20, yPosition);
        yPosition += 10;
        
        tables.forEach(table => {
          if (yPosition > pageHeight - 50) {
            pdf.addPage();
            yPosition = 20;
          }
          
          if (table.title) {
            pdf.setFontSize(12);
            pdf.text(table.title, 20, yPosition);
            yPosition += 8;
          }
          
          // Add table data as text (simplified)
          pdf.setFontSize(10);
          if (table.headers) {
            pdf.text(table.headers.join(' | '), 20, yPosition);
            yPosition += 6;
          }
          
          if (table.rows) {
            table.rows.slice(0, 10).forEach(row => {
              if (yPosition > pageHeight - 20) {
                pdf.addPage();
                yPosition = 20;
              }
              pdf.text(row.join(' | '), 20, yPosition);
              yPosition += 5;
            });
          }
          
          yPosition += 10;
        });
      }
      
      // Save the PDF
      pdf.save(`report-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const formatLabel = (label: string): string => {
    // Convert snake_case or camelCase to Title Case
    return label
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .filter(word => word.length > 0)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const prepareChartData = (chart: ChartData): any[] => {
    const { labels, datasets, data } = chart;
    
    // Handle Graphy format (labels + datasets)
    if (labels && datasets && datasets.length > 0) {
      return labels.map((label, idx) => {
        const dataPoint: any = { name: label };
        datasets.forEach(ds => {
          const key = formatLabel(ds.label || ds.name || 'Value');
          dataPoint[key] = ds.data && ds.data[idx] !== undefined ? ds.data[idx] : 0;
        });
        return dataPoint;
      });
    }
    
    // Handle direct data array
    if (Array.isArray(data)) {
      return data.map((item, itemIdx) => {
        if (typeof item === 'object') {
          const result: any = {};
          
          // Extract name/label - avoid generic labels
          const nameField = item.label || item.name || item.category || item.segment || 
                           item.trend || item.month || item.system || item.area ||
                           item.metric || item.feature || item.component || item.type ||
                           item.dependency || item.title || item.description || item.key ||
                           item.factor || item.dimension || item.aspect || item.element ||
                           item.product || item.service || item.region || item.channel;
          
          // If still no name field, try to use the first string property
          let finalName = nameField;
          if (!finalName) {
            for (const [key, value] of Object.entries(item)) {
              if (typeof value === 'string' && key !== 'value' && key !== 'color') {
                finalName = value;
                break;
              }
            }
          }
          
          result.name = finalName || `Item ${itemIdx + 1}`;
          
          // Handle special chart types based on data structure
          if ('trend' in item && ('impact' in item || 'score' in item)) {
            // Trend impact chart - show both impact level and score
            result.name = item.trend || `Trend ${itemIdx + 1}`;
            if (item.score !== undefined) {
              result.Score = item.score;
            }
            if (item.impact !== undefined) {
              // Convert impact level to numeric if it's a string
              const impactValue = typeof item.impact === 'string' ? 
                (item.impact.toLowerCase() === 'high' ? 9 : 
                 item.impact.toLowerCase() === 'medium' ? 6 : 
                 item.impact.toLowerCase() === 'low' ? 3 : 0) : item.impact;
              result.Impact = impactValue;
            }
            // Add timeline as a label, not a value
            if (item.timeline) {
              result.name = `${result.name} (${item.timeline})`;
            }
            return result;
          }
          
          if ('market_cap' in item || 'revenue' in item || 'growth' in item) {
            // Competitor metrics
            result.name = item.name && item.name !== 'Unknown' ? item.name : `Company ${itemIdx + 1}`;
            if (item.market_cap !== undefined) result['Market Cap'] = item.market_cap;
            if (item.revenue !== undefined) result.Revenue = item.revenue;
            if (item.growth !== undefined) result.Growth = item.growth;
            return result;
          }
          
          if ('satisfaction' in item || 'nps' in item || 'loyalty' in item) {
            // Customer insights
            result.name = item.segment || item.category || `Segment ${itemIdx + 1}`;
            if (item.satisfaction !== undefined) result.Satisfaction = item.satisfaction;
            if (item.nps !== undefined) result.NPS = item.nps;
            if (item.loyalty !== undefined) result.Loyalty = item.loyalty;
            if (item.value !== undefined && !result.Satisfaction) result.Score = item.value;
            return result;
          }
          
          // Generic handling - extract all numeric fields with proper names
          const excludeFields = ['label', 'name', 'category', 'segment', 'trend', 'month', 'system', 'area'];
          let hasValues = false;
          
          Object.keys(item).forEach(key => {
            if (!excludeFields.includes(key)) {
              const value = item[key];
              if (typeof value === 'number' && !isNaN(value)) {
                result[formatLabel(key)] = value;
                hasValues = true;
              } else if (key === 'value' && value !== undefined && value !== null) {
                // Only use 'value' if we don't have other numeric fields
                if (!hasValues) {
                  const valueLabel = chart.yAxis || 
                    (chart.title?.toLowerCase().includes('revenue') ? 'Revenue' :
                     chart.title?.toLowerCase().includes('cost') ? 'Cost' :
                     chart.title?.toLowerCase().includes('satisfaction') ? 'Satisfaction' :
                     chart.title?.toLowerCase().includes('score') ? 'Score' :
                     chart.title?.toLowerCase().includes('performance') ? 'Performance' :
                     chart.title?.toLowerCase().includes('investment') ? 'Investment' :
                     'Value');
                  result[valueLabel] = parseFloat(value) || 0;
                }
              }
            }
          });
          
          return result;
        } else if (typeof item === 'string') {
          // If the item is a string, use it as the name
          return { name: item, Value: 1 };
        }
        return { name: `Item ${itemIdx + 1}`, Value: item };
      });
    }
    
    // Handle object data
    if (typeof data === 'object' && data !== null) {
      return Object.entries(data).map(([key, value]) => ({
        name: formatLabel(key),
        [chart.yAxis || 'Value']: typeof value === 'number' ? value : 0
      }));
    }
    
    return [];
  };

  // Helper function to format JSON data as readable text
  const formatJsonAsText = (data: any): string => {
    if (!data) return '';
    
    // If it's already a string, check if it's JSON
    if (typeof data === 'string') {
      // First check if it contains JSON-like patterns
      if (data.includes('"main_analysis":') || data.includes('"market_trends":') || 
          data.includes('"key_findings":') || data.includes('"trend":')) {
        // This is a JSON string that wasn't properly parsed
        // Try to extract the content
        
        // Extract main_analysis if present
        const mainAnalysisMatch = data.match(/"main_analysis":\s*"([^"]+(?:\\.[^"]+)*)"/);
        if (mainAnalysisMatch) {
          return mainAnalysisMatch[1].replace(/\\"/g, '"').replace(/\\n/g, ' ');
        }
        
        // Extract market_trends if present
        const marketTrendsMatch = data.match(/"market_trends":\s*\[(.*?)\]/s);
        if (marketTrendsMatch) {
          try {
            const trendsJson = JSON.parse(`[${marketTrendsMatch[1]}]`);
            const trends = trendsJson.map((trend: any) => {
              const parts = [];
              if (trend.trend) parts.push(trend.trend);
              if (trend.impact) parts.push(`Impact: ${trend.impact}`);
              if (trend.timeline) parts.push(`Timeline: ${trend.timeline}`);
              return parts.join(' - ');
            });
            return 'Market trends: ' + trends.join('; ');
          } catch {
            // If parsing fails, try to extract manually
            const trends = [];
            const trendMatches = marketTrendsMatch[1].matchAll(/"trend":\s*"([^"]+)"/g);
            for (const match of trendMatches) {
              trends.push(match[1]);
            }
            if (trends.length > 0) {
              return 'Market trends: ' + trends.join('; ');
            }
          }
        }
        
        // If the string contains incomplete JSON (cut off), try to extract what we can
        if (data.includes('This presents significant opportunity')) {
          // This is likely truncated JSON with added text
          // Extract the readable parts
          const cleanedData = data.replace(/"[^"]*":\s*/g, '').replace(/[{}\[\],]/g, '').trim();
          return cleanedData;
        }
      }
      
      // Check if it looks like complete JSON
      if (data.trim().startsWith('{') || data.trim().startsWith('[')) {
        try {
          const parsed = JSON.parse(data);
          return formatJsonAsText(parsed); // Recursively format the parsed JSON
        } catch {
          // Not valid JSON, return as is
          return data;
        }
      }
      return data;
    }
    
    // Handle object/dict
    if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
      // Look for main_analysis first
      if (data.main_analysis) {
        return data.main_analysis;
      }
      
      // Look for description or summary
      if (data.description) return data.description;
      if (data.summary) return data.summary;
      
      // Handle market_trends
      if (data.market_trends && Array.isArray(data.market_trends)) {
        const trends = data.market_trends.map((trend: any) => {
          if (typeof trend === 'object') {
            const parts = [];
            if (trend.trend) parts.push(trend.trend);
            if (trend.impact) parts.push(`Impact: ${trend.impact}`);
            if (trend.timeline) parts.push(`Timeline: ${trend.timeline}`);
            return parts.join(' - ');
          }
          return String(trend);
        });
        return 'Market trends: ' + trends.join('; ');
      }
      
      // Handle key_findings
      if (data.key_findings && Array.isArray(data.key_findings)) {
        return 'Key findings: ' + data.key_findings.join('; ');
      }
      
      // Generic object handling - extract meaningful fields
      const meaningfulParts = [];
      for (const [key, value] of Object.entries(data)) {
        if (key !== 'sources' && key !== 'metadata' && key !== 'raw_data' && value) {
          if (typeof value === 'string' || typeof value === 'number') {
            const readableKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            meaningfulParts.push(`${readableKey}: ${value}`);
          } else if (Array.isArray(value) && value.length > 0) {
            const readableKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            if (value.every(item => typeof item === 'string')) {
              meaningfulParts.push(`${readableKey}: ${value.slice(0, 3).join(', ')}`);
            }
          }
        }
      }
      
      if (meaningfulParts.length > 0) {
        return meaningfulParts.join('. ');
      }
      
      // Last resort - stringify but clean up
      return JSON.stringify(data, null, 2);
    }
    
    // Handle array
    if (Array.isArray(data)) {
      if (data.every(item => typeof item === 'string')) {
        return data.join('; ');
      }
      
      const formattedItems = data.slice(0, 5).map((item: any) => {
        if (typeof item === 'object' && item !== null) {
          return item.description || item.name || item.value || JSON.stringify(item);
        }
        return String(item);
      });
      return formattedItems.join('; ');
    }
    
    // For any other type, convert to string
    return String(data);
  };

  const renderChartMetadata = (chart: ChartData) => {
    const sources = chart.sources || [];
    const insightDetails = formatJsonAsText(chart.insight_details || chart.insightDetails || '');
    const strategicImplication = formatJsonAsText(chart.strategic_implication || chart.strategicImplication || '');
    
    if (!sources.length && !insightDetails && !strategicImplication) {
      return null;
    }
    
    // Function to determine if source is a website or file and create appropriate link
    const renderSourceLink = (source: string) => {
      // Clean up source format: extract URL and get correct domain
      let cleanSource = source;
      let displayUrl = source;
      
      // Handle format like "cnbc.com - https://www.sec.gov/..." 
      if (source.includes(' - http')) {
        const parts = source.split(' - ');
        if (parts.length >= 2 && parts[1].startsWith('http')) {
          displayUrl = parts[1]; // Use the actual URL
          cleanSource = parts[1]; // Display the actual URL
        }
      }
      
      // Check if it's a website (contains www. or starts with http)
      if (cleanSource.includes('www.') || cleanSource.startsWith('http')) {
        // Ensure proper URL format
        let url = displayUrl;
        if (!url.startsWith('http')) {
          url = 'https://' + url;
        }
        
        // Extract domain for display
        let displayText = cleanSource;
        try {
          const urlObj = new URL(url);
          displayText = url; // Show the full URL instead of just domain
        } catch (e) {
          // If URL parsing fails, use original
          displayText = cleanSource;
        }
        
        return (
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="source-link external-link"
            title={`View external source: ${displayText}`}
          >
            {displayText}
          </a>
        );
      }
      // Check if it's a file (has file extension)
      else if (source.match(/\.(pdf|xlsx|docx|csv|json|txt|doc|xls)$/i)) {
        // For actual filenames, create download functionality
        return (
          <span className="source-link file-download" title={`Original file: ${source}`}>
            <span className="filename">{source}</span>
            <button 
              className="download-btn"
              onClick={async () => {
                try {
                  // Try to find the file by name and download it
                  // This would require an API endpoint to lookup file by name
                  console.log(`Download requested for file: ${source}`);
                  // For now, link to file management search
                  window.open(`/files?search=${encodeURIComponent(source)}`, '_blank');
                } catch (error) {
                  console.error('Download failed:', error);
                }
              }}
              title="View in File Management"
            >
              📄
            </button>
          </span>
        );
      }
      // Check if it's a document ID format (category_doc_id or doc_id)
      else if (source.match(/^[a-zA-Z_]*_doc_[a-zA-Z0-9-]+$/) || source.match(/^doc_[a-zA-Z0-9-]+$/)) {
        // This is a document ID format - show as searchable document
        return (
          <span className="source-link document-reference" title={`Document reference: ${source}`}>
            <span className="document-id">{source.replace(/_/g, ' ')}</span>
            <button 
              className="search-btn"
              onClick={async () => {
                try {
                  console.log(`Search requested for document: ${source}`);
                  // Search for the document in file management
                  window.open(`/files?search=${encodeURIComponent(source)}`, '_blank');
                } catch (error) {
                  console.error('Search failed:', error);
                }
              }}
              title="Search in File Management"
            >
              🔍
            </button>
          </span>
        );
      }
      // For other sources (like agent names), just display as text
      else {
        return <span className="source-text">{source}</span>;
      }
    };
    
    return (
      <div className="chart-metadata">
        {sources.length > 0 && (
          <div className="chart-sources">
            <strong>Data Sources:</strong>
            <ul className="sources-list">
              {sources.map((source, idx) => (
                <li key={idx}>
                  {renderSourceLink(source)}
                </li>
              ))}
            </ul>
          </div>
        )}
        {insightDetails && (
          <div className="chart-insight">
            <strong>Insight:</strong>
            <p>{insightDetails}</p>
          </div>
        )}
        {strategicImplication && (
          <div className="chart-implication">
            <strong>Strategic Implication for FAB:</strong>
            <p>{strategicImplication}</p>
          </div>
        )}
      </div>
    );
  };

  const renderChart = (chart: ChartData, index: number) => {
    let chartType = (chart.chart_type || chart.type || 'bar').toLowerCase();
    const chartTitle = chart.chart_title || chart.title || 'Chart';
    
    // Check if this is a table type with table_data
    if (chartType === 'table' && chart.table_data) {
      const { headers, rows } = chart.table_data;
      
      if (!headers || !rows || rows.length === 0) {
        return (
          <div key={index} className="chart-container">
            <h4 className="chart-title">{chartTitle}</h4>
            <div className="no-data">No table data available</div>
          </div>
        );
      }
      
      return (
        <div key={index} className="chart-container table-chart-container">
          <h4 className="chart-title">{chartTitle}</h4>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  {headers.map((header, idx) => (
                    <th key={idx}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {row.map((cell, cellIdx) => (
                      <td key={cellIdx}>
                        {typeof cell === 'object' && cell !== null ? (
                          // Handle objects with label, value, subtext structure
                          cell.label ? (
                            <div>
                              <strong>{cell.label}:</strong> {cell.value}
                              {cell.subtext && <div className="cell-subtext">{cell.subtext}</div>}
                            </div>
                          ) : (
                            // Fallback for other objects
                            JSON.stringify(cell)
                          )
                        ) : (
                          // Render primitive values directly
                          cell
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderChartMetadata(chart)}
        </div>
      );
    }
    
    // Better chart type detection based on data and title
    const titleLower = chartTitle.toLowerCase();
    const hasTimeData = chart.data && Array.isArray(chart.data) && 
                       chart.data.some((d: any) => d.month || d.quarter || d.year || d.timeline);
    
    // Auto-detect chart type
    if (chartType === 'bar' || chartType === 'column') {
      // Override bar charts for specific cases
      // Check for Performance vs Investment Analysis
      if ((titleLower.includes('performance') && titleLower.includes('investment')) ||
          (titleLower.includes('vs') && (titleLower.includes('performance') || titleLower.includes('investment')))) {
        chartType = 'scatter';
      } else if (titleLower.includes('waterfall') || titleLower.includes('cascade')) {
        chartType = 'waterfall';
      } else if (titleLower.includes('funnel') || titleLower.includes('conversion') || titleLower.includes('acquisition')) {
        chartType = 'funnel';
      } else if ((titleLower.includes('growth') || titleLower.includes('adoption') || 
           titleLower.includes('forecast') || titleLower.includes('curve')) && 
          !titleLower.includes('impact')) {
        chartType = 'line';
      } else if (titleLower.includes('distribution') || titleLower.includes('composition') ||
                 titleLower.includes('breakdown') || titleLower.includes('share')) {
        chartType = 'pie';
      } else if (titleLower.includes('comparison') && chart.data?.length > 0 && 
                 Object.keys(chart.data[0] || {}).length > 3) {
        chartType = 'radar';
      }
    }
    
    // Handle heatmap as a special bar chart with colors
    if (chartType === 'heatmap') {
      chartType = 'bar';
    }
    
    // Debug logging
    console.log(`Chart ${index}: ${chartTitle}`);
    console.log('  Type:', chartType);
    console.log('  Raw data:', chart.data);
    console.log('  Labels:', chart.labels);
    console.log('  Datasets:', chart.datasets);
    
    // Log dataset details if available
    if (chart.datasets && chart.datasets.length > 0) {
      chart.datasets.forEach((dataset: any, dsIdx: number) => {
        console.log(`  Dataset ${dsIdx} details:`, {
          label: dataset.label,
          hasData: !!dataset.data,
          dataLength: dataset.data ? dataset.data.length : 0,
          dataContent: dataset.data,
          backgroundColor: dataset.backgroundColor,
          borderColor: dataset.borderColor
        });
        // For scatter charts, log the actual data structure
        if ((chartType === 'scatter' || chartType === 'scatterplot') && dataset.data) {
          console.log(`  Scatter data structure:`, dataset.data);
          if (Array.isArray(dataset.data) && dataset.data.length > 0) {
            console.log(`  First data point:`, dataset.data[0]);
          }
        }
      });
    }
    
    const chartData = prepareChartData(chart);
    console.log('  Prepared data:', chartData);
    
    if (chartData.length === 0) {
      return (
        <div key={index} className="chart-container">
          <h4 className="chart-title">{chartTitle}</h4>
          <div className="no-data">No data available</div>
        </div>
      );
    }
    
    const containerProps = { width: '100%', height: 300 };
    
    switch (chartType) {
      case 'pie':
      case 'piechart':
      case 'pie_chart':
      case 'donut':
        // For pie charts, find the value field dynamically
        const pieDataKey = chartData.length > 0 ? 
          Object.keys(chartData[0]).find(k => k !== 'name') || 'value' : 'value';
        
        return (
          <div key={index} className="chart-container">
            <h4 className="chart-title">{chartTitle}</h4>
            <ResponsiveContainer {...containerProps}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey={pieDataKey}
                >
                  {chartData.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            {renderChartMetadata(chart)}
          </div>
        );
      
      case 'bar':
      case 'barchart':
      case 'bar_chart':
      case 'column':
        return (
          <div key={index} className="chart-container">
            <h4 className="chart-title">{chartTitle}</h4>
            <ResponsiveContainer {...containerProps}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                {(() => {
                  // Get all data keys except 'name'
                  const dataKeys = chartData.length > 0 ? 
                    Object.keys(chartData[0]).filter(k => k !== 'name') : [];
                  
                  if (dataKeys.length > 1) {
                    // Multiple series
                    return dataKeys.map((key, idx) => (
                      <Bar 
                        key={key}
                        dataKey={key}
                        fill={COLORS[idx % COLORS.length]}
                        name={key}
                      />
                    ));
                  } else if (dataKeys.length === 1) {
                    // Single series
                    return <Bar dataKey={dataKeys[0]} fill={COLORS[0]} name={dataKeys[0]} />;
                  } else {
                    // Fallback
                    return <Bar dataKey="value" fill={COLORS[0]} />;
                  }
                })()}
              </BarChart>
            </ResponsiveContainer>
            {renderChartMetadata(chart)}
          </div>
        );
      
      case 'line':
      case 'linechart':
      case 'line_chart':
      case 'linegraph':
        return (
          <div key={index} className="chart-container">
            <h4 className="chart-title">{chartTitle}</h4>
            <ResponsiveContainer {...containerProps}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                {(() => {
                  // Get all data keys except 'name'
                  const dataKeys = chartData.length > 0 ? 
                    Object.keys(chartData[0]).filter(k => k !== 'name') : [];
                  
                  if (dataKeys.length > 1) {
                    // Multiple series
                    return dataKeys.map((key, idx) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={COLORS[idx % COLORS.length]}
                        strokeWidth={2}
                        name={key}
                        dot={{ fill: COLORS[idx % COLORS.length], r: 3 }}
                      />
                    ));
                  } else if (dataKeys.length === 1) {
                    // Single series
                    return <Line 
                      type="monotone" 
                      dataKey={dataKeys[0]} 
                      stroke={COLORS[0]} 
                      strokeWidth={2}
                      name={dataKeys[0]}
                      dot={{ fill: COLORS[0], r: 3 }}
                    />;
                  } else {
                    // Fallback
                    return <Line type="monotone" dataKey="value" stroke={COLORS[0]} strokeWidth={2} />;
                  }
                })()}
              </LineChart>
            </ResponsiveContainer>
            {renderChartMetadata(chart)}
          </div>
        );
      
      case 'area':
      case 'areachart':
      case 'area_chart':
        return (
          <div key={index} className="chart-container">
            <h4 className="chart-title">{chartTitle}</h4>
            <ResponsiveContainer {...containerProps}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="value" stroke={COLORS[0]} fill={COLORS[0]} />
              </AreaChart>
            </ResponsiveContainer>
            {renderChartMetadata(chart)}
          </div>
        );
      
      case 'scatter':
      case 'scatterplot':
      case 'scatter_plot':
        // Handle Performance vs Investment type charts
        let scatterData = [];
        
        // Check if we have datasets with nested data (like the scatter chart format)
        if (chart.datasets && chart.datasets.length > 0) {
          const dataset = chart.datasets[0];
          // Check if dataset has data property with actual values
          if (dataset.data && Array.isArray(dataset.data) && dataset.data.length > 0) {
            // Check the structure of the first data point
            const firstPoint = dataset.data[0];
            console.log('Scatter - first data point structure:', firstPoint);
            
            if (typeof firstPoint === 'object' && (firstPoint.x !== undefined && firstPoint.y !== undefined)) {
              // Data is already in x/y format
              console.log('Scatter data in x/y format, converting...');
              scatterData = dataset.data.map((item: any, idx: number) => ({
                x: item.x || 0,
                y: item.y || 0,
                name: item.label || item.name || `Point ${idx + 1}`
              }));
              console.log('Converted scatter data:', scatterData);
            } else if (typeof firstPoint === 'number') {
              // Data is just an array of numbers - need to pair with labels or create x/y pairs
              if (chart.labels && chart.labels.length === dataset.data.length) {
                // Use labels as names and data as y-values, with index as x
                scatterData = dataset.data.map((value: number, idx: number) => ({
                  x: idx + 1,
                  y: value,
                  name: chart.labels?.[idx] || `Point ${idx + 1}`
                }));
              } else {
                // Create x/y pairs from sequential data
                scatterData = dataset.data.map((value: number, idx: number) => ({
                  x: idx + 1,
                  y: value,
                  name: `Point ${idx + 1}`
                }));
              }
            } else {
              // Try to extract numeric values from objects
              scatterData = dataset.data.map((item: any, idx: number) => {
                // Look for numeric properties to use as x and y
                const numericProps = Object.entries(item || {})
                  .filter(([key, value]) => typeof value === 'number')
                  .map(([key, value]) => ({ key, value }));
                
                return {
                  x: item.investment || item.Investment || numericProps[0]?.value || idx + 1,
                  y: item.performance || item.Performance || item.return || item.Return || numericProps[1]?.value || numericProps[0]?.value || 0,
                  name: item.name || item.label || chart.labels?.[idx] || `Point ${idx + 1}`
                };
              });
            }
          } 
          // If no data but we have labels, generate sample scatter data
          else if (chart.labels && Array.isArray(chart.labels)) {
            // For now, generate placeholder scatter points based on labels
            // This should be replaced with actual data from the backend
            console.warn('Scatter chart has no data points, generating placeholders');
            scatterData = chart.labels.map((label: string, idx: number) => ({
              x: (idx + 1) * 20,  // Spread points along x-axis
              y: 30 + Math.random() * 40,  // Random y values between 30-70
              name: label
            }));
          }
        } else if (chartData.length > 0) {
          // Use prepared chart data
          scatterData = chartData.map((item, idx) => {
            const numericFields = Object.entries(item)
              .filter(([key, value]) => typeof value === 'number' && key !== 'name')
              .map(([key, value]) => ({ key, value: value as number }));
            
            const xValue = item.x || item.investment || item.performance || 
                          item.Investment || item.Performance ||
                          (numericFields[0]?.value) || idx;
            const yValue = item.y || item.return || item.roi || item.value ||
                          item.Return || item.ROI || item.Value ||
                          (numericFields[1]?.value) || (numericFields[0]?.value) || 0;
            return {
              ...item,
              x: xValue,
              y: yValue,
              name: item.name || item.label || `Point ${idx + 1}`
            };
          });
        } else if (chart.data && Array.isArray(chart.data)) {
          // Fallback to raw data
          scatterData = chart.data.map((item, idx) => {
            if (typeof item === 'object') {
              const numericFields = Object.entries(item)
                .filter(([key, value]) => typeof value === 'number' && key !== 'name' && key !== 'label')
                .map(([key, value]) => ({ key, value }));
              
              const xValue = item.x || item.investment || item.Investment || 
                            item.performance || item.Performance ||
                            (numericFields[0]?.value) || idx;
              const yValue = item.y || item.return || item.Return || 
                            item.roi || item.ROI || item.value || item.Value ||
                            (numericFields[1]?.value) || (numericFields[0]?.value) || 0;
              
              return {
                name: item.name || item.label || `Point ${idx + 1}`,
                x: xValue,
                y: yValue
              };
            }
            return { name: `Point ${idx + 1}`, x: idx, y: 0 };
          });
        }
        
        // Don't show empty scatter charts (but only if truly empty)
        if (!scatterData || scatterData.length === 0) {
          console.log('Skipping empty scatter chart:', chartTitle);
          return null;
        }
        
        // Check if all points are at origin (0,0) which indicates no real data
        const hasRealData = scatterData.some((point: any) => point.x !== 0 || point.y !== 0);
        if (!hasRealData) {
          console.log('Skipping scatter chart with all points at origin:', chartTitle);
          return null;
        }
        
        return (
          <div key={index} className="chart-container">
            <h4 className="chart-title">{chartTitle}</h4>
            <ResponsiveContainer {...containerProps}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  dataKey="x" 
                  name={chart.xAxis || 'Investment'} 
                  label={{ value: chart.xAxis || 'Investment', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="y" 
                  name={chart.yAxis || 'Performance'} 
                  label={{ value: chart.yAxis || 'Performance', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const data = payload[0].payload;
                      return (
                        <div style={{ backgroundColor: 'white', padding: '5px', border: '1px solid #ccc' }}>
                          <p><strong>{data.name}</strong></p>
                          <p>{chart.xAxis || 'Investment'}: {data.x}</p>
                          <p>{chart.yAxis || 'Performance'}: {data.y}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Scatter name={chartTitle} data={scatterData} fill={COLORS[0]}>
                  {scatterData.map((entry: any, idx: number) => (
                    <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
            {renderChartMetadata(chart)}
          </div>
        );
      
      case 'radar':
      case 'radarchart':
        // Find the value field dynamically for radar charts
        const radarValueKey = chartData.length > 0 ? 
          Object.keys(chartData[0]).find(k => k !== 'name' && typeof chartData[0][k] === 'number') || 'value' : 'value';
        
        return (
          <div key={index} className="chart-container">
            <h4 className="chart-title">{chartTitle}</h4>
            <ResponsiveContainer {...containerProps}>
              <RadarChart data={chartData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="name" />
                <PolarRadiusAxis domain={[0, 100]} />
                <Radar 
                  name={chartTitle} 
                  dataKey={radarValueKey} 
                  stroke={COLORS[0]} 
                  fill={COLORS[0]} 
                  fillOpacity={0.6} 
                />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
            {renderChartMetadata(chart)}
          </div>
        );
      
      case 'waterfall':
      case 'waterfall_chart':
        // Improved waterfall chart implementation based on best practices
        let runningTotal = 0;
        const waterfallData = chartData.map((item, idx, arr) => {
          // Try multiple possible value fields for better compatibility
          const currentValue = item.value || item.Value || item.amount || item.change || 
                               Object.values(item).find(v => typeof v === 'number') || 0;
          const previousTotal = runningTotal;
          runningTotal += currentValue;
          
          // Check if this is a total bar
          const isTotal = item.name?.toLowerCase().includes('total') || 
                         item.name?.toLowerCase().includes('final') ||
                         idx === arr.length - 1;
          
          // Create proper waterfall data structure
          if (isTotal) {
            return {
              name: item.name || 'Total',
              // For total bars, show from 0 to total
              base: 0,
              end: runningTotal,
              value: runningTotal,
              actualValue: runningTotal,
              total: runningTotal,
              fill: runningTotal >= 0 ? '#0088FE' : '#FF8042',
              isPositive: runningTotal >= 0,
              isTotal: true,
              // Bar positioning
              positive: runningTotal > 0 ? runningTotal : 0,
              negative: runningTotal < 0 ? Math.abs(runningTotal) : 0,
              invisible: 0
            };
          } else {
            // Regular waterfall bars
            return {
              name: item.name,
              base: Math.min(previousTotal, runningTotal),
              end: Math.max(previousTotal, runningTotal),
              value: currentValue,
              actualValue: currentValue,
              total: runningTotal,
              fill: currentValue >= 0 ? '#00C49F' : '#FF8042',
              isPositive: currentValue >= 0,
              isTotal: false,
              // Bar positioning - invisible bar positions the actual bar
              positive: currentValue > 0 ? currentValue : 0,
              negative: currentValue < 0 ? Math.abs(currentValue) : 0,
              invisible: currentValue >= 0 ? previousTotal : runningTotal
            };
          }
        });
        
        return (
          <div key={index} className="chart-container">
            <h4 className="chart-title">{chartTitle}</h4>
            <ResponsiveContainer {...containerProps}>
              <ComposedChart data={waterfallData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const data = payload[0].payload;
                      return (
                        <div style={{ backgroundColor: 'white', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
                          <p style={{ fontWeight: 'bold', margin: '0 0 5px 0' }}>{data.name}</p>
                          {!data.isTotal && (
                            <p style={{ margin: '3px 0', color: data.isPositive ? '#00C49F' : '#FF8042' }}>
                              Change: {data.isPositive && data.value > 0 ? '+' : ''}{data.actualValue.toLocaleString()}
                            </p>
                          )}
                          <p style={{ margin: '3px 0', fontWeight: data.isTotal ? 'bold' : 'normal' }}>
                            {data.isTotal ? 'Final Total: ' : 'Running Total: '}{data.total.toLocaleString()}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                {/* Invisible bars for positioning */}
                <Bar dataKey="invisible" stackId="stack" fill="transparent" />
                {/* Positive changes */}
                <Bar dataKey="positive" stackId="stack" fill="#00C49F" name="Increase" />
                {/* Negative changes */}
                <Bar dataKey="negative" stackId="stack" fill="#FF8042" name="Decrease" />
                {/* Reference line at zero */}
                <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
              </ComposedChart>
            </ResponsiveContainer>
            {renderChartMetadata(chart)}
          </div>
        );
      
      case 'funnel':
      case 'funnel_chart':
        // Funnel chart using bar chart with decreasing widths
        let funnelData: any[] = [];
        
        console.log('Funnel chart data structure:', {
          hasLabels: !!chart.labels,
          hasDatasets: !!chart.datasets,
          datasetCount: chart.datasets?.length,
          firstDataset: chart.datasets?.[0]
        });
        
        // Check if we have the labels/datasets format (like the funnel chart data)
        if (chart.labels && chart.datasets && chart.datasets.length > 0) {
          const dataset = chart.datasets[0];
          if (Array.isArray(dataset.data) && Array.isArray(chart.labels)) {
            console.log('Using labels/datasets format for funnel');
            funnelData = chart.labels.map((label, idx) => ({
              name: label,
              value: dataset.data[idx] || 0,
              fill: dataset.backgroundColor ? 
                (Array.isArray(dataset.backgroundColor) ? 
                  dataset.backgroundColor[idx] || COLORS[idx % COLORS.length] : 
                  dataset.backgroundColor) : 
                COLORS[idx % COLORS.length]
            }));
          }
        } else if (chartData.length > 0) {
          // Use prepared chart data
          funnelData = chartData.map((item, idx) => {
            const valueField = Object.keys(item).find(k => k !== 'name' && typeof item[k] === 'number') || 'value';
            return {
              ...item,
              value: item[valueField] || item.value || item.Value || 0,
              fill: COLORS[idx % COLORS.length]
            };
          });
        } else if (chart.data && Array.isArray(chart.data)) {
          // Fallback to raw data
          funnelData = chart.data.map((item, idx) => {
            if (typeof item === 'object') {
              let value = item.value || item.Value || item.count || item.Count || 
                         item.amount || item.Amount || item.percentage || item.Percentage;
              
              if (value === undefined) {
                for (const [key, val] of Object.entries(item)) {
                  if (typeof val === 'number' && key !== 'index' && key !== 'id') {
                    value = val;
                    break;
                  }
                }
              }
              
              return {
                name: item.name || item.label || item.stage || item.Stage || `Stage ${idx + 1}`,
                value: value || 0,
                fill: COLORS[idx % COLORS.length]
              };
            }
            return { name: `Stage ${idx + 1}`, value: 0, fill: COLORS[idx % COLORS.length] };
          });
        }
        
        // Don't sort funnel data - keep the order as provided (awareness -> conversion)
        
        return (
          <div key={index} className="chart-container">
            <h4 className="chart-title">{chartTitle}</h4>
            <ResponsiveContainer {...containerProps}>
              <FunnelChart>
                <Tooltip 
                  formatter={(value: any) => value.toLocaleString()}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <Funnel
                  dataKey="value"
                  data={funnelData}
                  isAnimationActive
                  shape={(props: any) => {
                    const { x, y, width, height, payload, index } = props;
                    const percentage = funnelData[0]?.value ? 
                      Math.round((payload.value / funnelData[0].value) * 100) : 100;
                    return (
                      <g>
                        <path
                          d={`M ${x},${y} L ${x + width},${y} L ${x + width},${y + height} L ${x},${y + height} Z`}
                          fill={payload.fill || COLORS[index % COLORS.length]}
                        />
                        <text
                          x={x + width / 2}
                          y={y + height / 2 - 10}
                          fill="#fff"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="14"
                          fontWeight="bold"
                        >
                          {payload.name}
                        </text>
                        <text
                          x={x + width / 2}
                          y={y + height / 2 + 10}
                          fill="#fff"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="12"
                        >
                          {payload.value.toLocaleString()} ({percentage}%)
                        </text>
                      </g>
                    );
                  }}
                >
                  {funnelData.map((entry: any, idx: number) => (
                    <Cell key={`cell-${idx}`} fill={entry.fill || COLORS[idx % COLORS.length]} />
                  ))}
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
            {renderChartMetadata(chart)}
          </div>
        );
      
      case 'combo':
      case 'combo_chart':
      case 'composed':
        // Combo chart with bars and line - handle datasets structure
        const comboData = (() => {
          // If we have the proper datasets structure from the API
          if (chart.datasets && chart.labels) {
            return chart.labels.map((label: string, idx: number) => {
              const point: any = { name: label };
              chart.datasets?.forEach((dataset: any) => {
                point[dataset.label] = dataset.data[idx];
              });
              return point;
            });
          }
          // Otherwise use the regular data
          return chartData;
        })();
        
        return (
          <div key={index} className="chart-container">
            <h4 className="chart-title">{chartTitle}</h4>
            <ResponsiveContainer {...containerProps}>
              <ComposedChart data={comboData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                {chart.datasets ? (
                  // Handle structured datasets
                  chart.datasets.map((dataset: any, idx: number) => {
                    if (dataset.type === 'line') {
                      return (
                        <Line
                          key={dataset.label}
                          yAxisId={dataset.yAxisID || 'right'}
                          type="monotone"
                          dataKey={dataset.label}
                          stroke={dataset.borderColor || COLORS[idx % COLORS.length]}
                          strokeWidth={2}
                          name={dataset.label}
                          dot={{ r: 3 }}
                        />
                      );
                    } else {
                      return (
                        <Bar
                          key={dataset.label}
                          yAxisId={dataset.yAxisID || 'left'}
                          dataKey={dataset.label}
                          fill={dataset.backgroundColor || COLORS[idx % COLORS.length]}
                          name={dataset.label}
                        />
                      );
                    }
                  })
                ) : (
                  // Fallback to auto-detection
                  (() => {
                    const dataKeys = comboData.length > 0 ? 
                      Object.keys(comboData[0]).filter(k => k !== 'name') : [];
                    
                    if (dataKeys.length >= 2) {
                      return (
                        <>
                          <Bar 
                            yAxisId="left"
                            dataKey={dataKeys[0]} 
                            fill={COLORS[0]} 
                            name={dataKeys[0]}
                          />
                          <Line 
                            yAxisId="right"
                            type="monotone" 
                            dataKey={dataKeys[1]} 
                            stroke={COLORS[1]} 
                            strokeWidth={2}
                            name={dataKeys[1]}
                            dot={{ fill: COLORS[1], r: 3 }}
                          />
                        </>
                      );
                    } else {
                      return <Bar yAxisId="left" dataKey="value" fill={COLORS[0]} />;
                    }
                  })()
                )}
              </ComposedChart>
            </ResponsiveContainer>
            {renderChartMetadata(chart)}
          </div>
        );
      
      default:
        // Default to bar chart for unknown types
        return (
          <div key={index} className="chart-container">
            <h4 className="chart-title">{chartTitle}</h4>
            <ResponsiveContainer {...containerProps}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                {(() => {
                  const dataKeys = chartData.length > 0 ? 
                    Object.keys(chartData[0]).filter(k => k !== 'name') : [];
                  const dataKey = dataKeys[0] || 'value';
                  return <Bar dataKey={dataKey} fill={COLORS[0]} name={dataKey} />;
                })()}
              </BarChart>
            </ResponsiveContainer>
            {renderChartMetadata(chart)}
          </div>
        );
    }
  };

  const renderTable = (table: TableData, index: number) => {
    if (!table.headers && !table.rows && !table.data) {
      return null;
    }
    
    return (
      <div key={index} className="table-container">
        {table.title && <h4 className="table-title">{table.title}</h4>}
        <div className="table-wrapper">
          <table className="data-table">
            {table.headers && (
              <thead>
                <tr>
                  {table.headers.map((header, idx) => (
                    <th key={idx}>{header}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {table.rows ? (
                table.rows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {row.map((cell, cellIdx) => (
                      <td key={cellIdx}>{cell}</td>
                    ))}
                  </tr>
                ))
              ) : table.data ? (
                table.data.map((item, rowIdx) => (
                  <tr key={rowIdx}>
                    {Object.values(item).map((value: any, cellIdx) => (
                      <td key={cellIdx}>{value}</td>
                    ))}
                  </tr>
                ))
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="chart-visualization">
      {canExport && (
        <div className="export-controls">
          <button 
            onClick={handleExportPDF} 
            disabled={exporting}
            className="export-button"
          >
            {exporting ? 'Exporting...' : 'Export as PDF'}
          </button>
          {onRefresh && (
            <button onClick={onRefresh} className="refresh-button">
              Refresh Data
            </button>
          )}
        </div>
      )}
      
      <div className="charts-grid">
        {charts.map((chart, index) => renderChart(chart, index))}
      </div>
      
      {tables.length > 0 && (
        <div className="tables-section">
          {tables.map((table, index) => renderTable(table, index))}
        </div>
      )}
    </div>
  );
};

export default ChartVisualization;